import React, { Fragment } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import PressureTestDashboard from '../../components/PressureTestDashboard/PressureTestDashboard';

const PressureTestPage = () => {
    usePageSeo({
        title: 'Pro Forma Pressure Test — LIHTC Underwriting Stress Test',
        description:
            'Pressure-test a LIHTC pro forma in your browser — driver sensitivity, downside scenarios, and Monte Carlo breach probability across DSCR, gap, and IRR.',
        keywords:
            'LIHTC pro forma, affordable housing underwriting, pro forma stress test, DSCR sensitivity analysis, Monte Carlo real estate, tax credit equity, funding gap, investor IRR, deal pressure test, BUILDLAB',
    });

    return (
        <Fragment>
            <Header />
            <main className="page_content">
                <PageTitle pageTitle={'Pro Forma Pressure Test'} pagesub={'LIHTC Underwriting'} pageTop={'Tool'} />

                {/* Visual explainer — what visitors get from the tool */}
                <section className="pte" aria-labelledby="pte-h2">
                    <style
                        dangerouslySetInnerHTML={{
                            __html: `
.pte{background:#fff;padding:clamp(36px,6vw,76px) clamp(16px,4vw,26px)}
.pte *{box-sizing:border-box}
.pte .pte__in{max-width:1140px;margin:0 auto}
.pte .pte__head{text-align:center;max-width:730px;margin:0 auto clamp(30px,5vw,56px)}
.pte .pte__kick{font-family:'Axiforma Bold',sans-serif;font-size:13px;letter-spacing:2.4px;color:#0044EB;text-transform:uppercase;margin:0}
.pte .pte__h2{font-family:'Axiforma Bold',sans-serif;font-size:clamp(26px,4.6vw,42px);color:#020842;line-height:1.08;margin:12px 0 0}
.pte .pte__sub{font-family:'Axiforma Regular',sans-serif;font-size:clamp(15px,2.2vw,18px);color:#54648A;line-height:1.55;margin:16px 0 0}
.pte .pte__steps{display:flex;flex-direction:column;gap:clamp(28px,4.5vw,52px)}
.pte .pte__step{display:flex;gap:clamp(22px,4vw,54px);align-items:center}
.pte .pte__step:nth-child(even){flex-direction:row-reverse}
.pte .pte__media{flex:1 1 0;min-width:0}
.pte .pte__img{width:100%;height:auto;border:1px solid #D7E2F1;border-radius:14px;box-shadow:0 20px 46px rgba(6,16,54,.14);display:block;background:#fff}
.pte .pte__body{flex:1 1 0;min-width:0}
.pte .pte__num{display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:11px;background:#0044EB;color:#fff;font-family:'Axiforma Bold',sans-serif;font-size:18px}
.pte .pte__t{font-family:'Axiforma Bold',sans-serif;font-size:clamp(21px,3vw,28px);color:#020842;line-height:1.12;margin:14px 0 10px}
.pte .pte__d{font-family:'Axiforma Regular',sans-serif;font-size:clamp(15px,2.2vw,18px);color:#54648A;line-height:1.6;margin:0}
.pte .pte__chips{display:flex;gap:8px;flex-wrap:wrap;margin:16px 0 0}
.pte .pte__chip{font-family:'Axiforma Bold',sans-serif;font-size:12.5px;letter-spacing:.5px;padding:7px 13px;border-radius:999px;color:#fff}
.pte .pte__wl{display:flex;align-items:center;gap:18px;background:#020842;border-radius:16px;padding:clamp(20px,3.5vw,28px) clamp(22px,4vw,32px);margin-top:clamp(30px,5vw,52px)}
.pte .pte__wlicon{flex:none;width:46px;height:46px;border-radius:12px;background:#0044EB;display:flex;align-items:center;justify-content:center}
.pte .pte__wlt{font-family:'Axiforma Bold',sans-serif;color:#fff;font-size:clamp(17px,2.6vw,21px);margin:0}
.pte .pte__wld{font-family:'Axiforma Regular',sans-serif;color:#9FB4FF;font-size:clamp(13.5px,2vw,15.5px);line-height:1.5;margin:5px 0 0}
@media(max-width:768px){.pte .pte__step,.pte .pte__step:nth-child(even){flex-direction:column}}
`,
                        }}
                    />
                    <div className="pte__in">
                        <div className="pte__head">
                            <div className="pte__kick">What you get</div>
                            <h2 className="pte__h2" id="pte-h2">A verdict you can hand to your committee</h2>
                            <div className="pte__sub">
                                Enter your pro forma and the Pressure Test stresses every assumption — then returns a clean,
                                white-labeled report built to make one call: build it, fix it, or pass.
                            </div>
                        </div>
                        <div className="pte__steps">
                            <div className="pte__step">
                                <div className="pte__media">
                                    <img className="pte__img" src="/images/proforma/verdict.png" alt="Sample report page showing the PROCEED verdict and the covenant scorecard — minimum DSCR, investor IRR and funding gap" loading="lazy" />
                                </div>
                                <div className="pte__body">
                                    <span className="pte__num">1</span>
                                    <h3 className="pte__t">The verdict, up top</h3>
                                    <div className="pte__d">
                                        Proceed, restructure, or pass — right beside the covenant scorecard: minimum DSCR,
                                        investor IRR and the funding gap. No digging required.
                                    </div>
                                    <div className="pte__chips">
                                        <span className="pte__chip" style={{ background: '#1F9D57' }}>PROCEED</span>
                                        <span className="pte__chip" style={{ background: '#E0822C' }}>RESTRUCTURE</span>
                                        <span className="pte__chip" style={{ background: '#E11D48' }}>PASS</span>
                                    </div>
                                </div>
                            </div>
                            <div className="pte__step">
                                <div className="pte__media">
                                    <img className="pte__img" src="/images/proforma/breaks.png" alt="Sample report charts — a tornado of ranked risk drivers and a Monte Carlo distribution of minimum DSCR" loading="lazy" />
                                </div>
                                <div className="pte__body">
                                    <span className="pte__num">2</span>
                                    <h3 className="pte__t">Where the deal breaks</h3>
                                    <div className="pte__d">
                                        Every driver ranked by how hard it pushes you toward a breach — plus a 4,000-run Monte
                                        Carlo showing the real probability the deal clears, not one tidy base case.
                                    </div>
                                </div>
                            </div>
                            <div className="pte__step">
                                <div className="pte__media">
                                    <img className="pte__img" src="/images/proforma/glossary.png" alt="Sample report glossary defining DSCR, investor IRR, covenants and other terms in plain English" loading="lazy" />
                                </div>
                                <div className="pte__body">
                                    <span className="pte__num">3</span>
                                    <h3 className="pte__t">So anyone can read it</h3>
                                    <div className="pte__d">
                                        A plain-English glossary defines every term — DSCR, IRR, covenants, Monte Carlo — so your
                                        whole committee reads the same numbers the same way. Stress scenarios and full assumptions
                                        are in there too.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="pte__wl">
                            <div className="pte__wlicon">
                                <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M7 11V8a5 5 0 0110 0v3" fill="none" stroke="#fff" strokeWidth="2.4" />
                                    <rect x="4.5" y="11" width="15" height="9.5" rx="2.2" fill="#fff" />
                                    <rect x="11" y="14.6" width="2" height="3.4" rx="1" fill="#0044EB" />
                                </svg>
                            </div>
                            <div>
                                <div className="pte__wlt">White-labeled to your shop</div>
                                <div className="pte__wld">
                                    Your logo on the cover and your project's numbers throughout — only a small BuildLab credit
                                    stays locked in the footer.
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Illustrative-model disclosure — sits on the dashboard's dark canvas */}
                <section style={{ background: '#F1F6FC', padding: 'clamp(14px,4vw,26px) clamp(14px,4vw,26px) 0' }}>
                    <div
                        style={{
                            background: 'rgba(0,68,235,0.06)',
                            border: '1px solid rgba(0,68,235,0.28)',
                            borderRadius: 12,
                            padding: '14px 18px',
                            color: '#1A2B57',
                            fontSize: 14,
                            lineHeight: 1.55,
                            maxWidth: 1180,
                            margin: '0 auto',
                        }}
                    >
                        <strong style={{ color: '#0044EB' }}>Illustrative model.</strong> This pressure-test runs on a
                        simplified, placeholder underwriting engine — loan sizing, equity timing and the IRR waterfall are
                        stand-ins, not your funded terms. Use it to <em>see how a deal bends under stress</em>, not as a
                        substitute for a fully underwritten pro forma.{' '}
                        <Link to="/contact" style={{ color: '#0044EB', fontWeight: 600 }}>
                            Talk to BUILDLAB
                        </Link>{' '}
                        to model your actual deal.
                    </div>
                </section>

                <section style={{ background: '#F1F6FC' }}>
                    <PressureTestDashboard />
                </section>

                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default PressureTestPage;
